import { PencilLine } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getVisualStyleLabel, type VisualStyleLabelId } from '../domain/style-labels/vocabulary';
import { parseStyleNarrative } from '../domain/style/narrative';
import type { StyleProfile } from '../domain/style/schema';

export function StyleCard({
  profile,
  compact = false,
  previews = [],
  showAdjustAction = true,
}: {
  profile: StyleProfile;
  compact?: boolean;
  previews?: string[];
  showAdjustAction?: boolean;
}) {
  const [openLabel, setOpenLabel] = useState<VisualStyleLabelId | null>(null);
  const selectedLabelIds = [
    ...profile.calibratedCoreLabelIds,
    ...profile.calibratedSupportingLabelIds,
  ];
  const narrative = parseStyleNarrative(profile.styleNarrative, selectedLabelIds);
  const activeLabel = openLabel ? getVisualStyleLabel(openLabel) : null;
  const candidate = openLabel
    ? profile.candidateLabels.find((item) => item.labelId === openLabel)
    : null;
  const related = activeLabel?.relatedLabelIds.map(getVisualStyleLabel).filter(Boolean).slice(0, 4);
  const toggle = (labelId: VisualStyleLabelId) =>
    setOpenLabel((current) => (current === labelId ? null : labelId));

  return (
    <article className={`style-card ${compact ? 'compact' : ''}`}>
      <span className="style-card-kicker">
        Visual reading /{' '}
        {profile.isMock
          ? 'Mock fixture'
          : profile.userAdjustments.edited
            ? 'Adjusted by you'
            : 'Controlled vocabulary'}
      </span>
      {previews.length > 0 && (
        <div
          className={`style-collage collage-${Math.min(previews.length, 5)}`}
          aria-label="Uploaded visual references"
        >
          {previews.map((url, index) => (
            <img key={url} src={url} alt={`Visual reference ${index + 1}`} />
          ))}
        </div>
      )}
      <div className="style-card-title-wrap">
        <span className="style-card-ghost" aria-hidden="true">
          THE
        </span>
        <h2>{getVisualStyleLabel(profile.calibratedCoreLabelIds[0]!)?.label ?? 'Your style'}</h2>
      </div>
      <p className="style-narrative">
        {narrative.parts.map((part, index) =>
          part.type === 'text' ? (
            <span key={`${index}-${part.value.slice(0, 8)}`}>{part.value}</span>
          ) : (
            <button
              className={`narrative-label keyword-${selectedLabelIds.indexOf(part.labelId) % 2 === 0 ? 'cyan' : 'pink'}`}
              key={`${part.labelId}-${index}`}
              type="button"
              aria-expanded={openLabel === part.labelId}
              onClick={() => toggle(part.labelId)}
            >
              {part.value}
            </button>
          ),
        )}
      </p>
      {activeLabel && (
        <div className="keyword-explanation" role="status">
          <span>Controlled visual label</span>
          <strong>{activeLabel.label}</strong>
          <p>{activeLabel.shortDescription}</p>
          {candidate?.evidence.length &&
          (candidate.averageConfidence > 0 || candidate.coverage > 0) ? (
            <p>
              <b>Visible evidence</b> {candidate.evidence.join(' · ')}
            </p>
          ) : (
            <p>Added through your Style Card refinement.</p>
          )}
          {candidate && (candidate.averageConfidence > 0 || candidate.coverage > 0) ? (
            <small>
              Image confidence {Math.round(candidate.averageConfidence * 100)}% · Coverage{' '}
              {Math.round(candidate.coverage * 100)}%
            </small>
          ) : null}
          {related?.length ? (
            <small>Related: {related.map((item) => item?.label).join(', ')}</small>
          ) : null}
        </div>
      )}
      {profile.uncertainty.length > 0 && (
        <details className="uncertainty">
          <summary>Where this reading is uncertain</summary>
          <ul>
            {profile.uncertainty.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </details>
      )}
      {showAdjustAction && (
        <Link className="button style-adjust-action" to="/style">
          <PencilLine /> Adjust the overall reading
        </Link>
      )}
    </article>
  );
}
